'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useAppStore } from '@/lib/store';
import { translations, Language } from '@/lib/translations';

interface HomeProps {
  lang: Language;
}

export function Home({ lang }: HomeProps) {
  const t = translations[lang];
  const { setActiveTab } = useAppStore();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const quickLinks = [
    { id: 'dashboard' as const, labelKey: 'dashboard', icon: 'Layout', description: lang === 'en' ? 'View project overview and statistics' : '查看项目概览和统计信息' },
    { id: 'cr_analysis' as const, labelKey: 'crAnalysis', icon: 'Target', description: lang === 'en' ? 'Analyze competitive reality' : '分析竞争现实' },
    { id: 'radar' as const, labelKey: 'radarPlot', icon: 'Hexagon', description: lang === 'en' ? 'Compare projects with radar charts' : '使用雷达图对比项目' },
    { id: 'strategy' as const, labelKey: 'strategyFunnel', icon: 'Target', description: lang === 'en' ? 'Strategic funnel analysis' : '战略漏斗分析' },
    { id: 'form' as const, labelKey: 'newScore', icon: 'Plus', description: lang === 'en' ? 'Create new project assessment' : '创建新项目评估' },
    { id: 'list' as const, labelKey: 'projects', icon: 'List', description: lang === 'en' ? 'Browse all projects' : '浏览所有项目' },
  ];

  const IconWrapper = ({ icon, className }: { icon: string; className?: string }) => {
    const icons: Record<string, () => JSX.Element> = {
      Layout: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
          <line x1="3" x2="21" y1="9" y2="9"/>
          <line x1="9" x2="9" y1="21" y2="9"/>
        </svg>
      ),
      Target: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2"/>
        </svg>
      ),
      Hexagon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        </svg>
      ),
      List: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" x2="21" y1="6" y2="6"/>
          <line x1="8" x2="21" y1="12" y2="12"/>
          <line x1="8" x2="21" y1="18" y2="18"/>
          <line x1="3" x2="3.01" y1="6" y2="6"/>
          <line x1="3" x2="3.01" y1="12" y2="12"/>
          <line x1="3" x2="3.01" y1="18" y2="18"/>
        </svg>
      ),
      Plus: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14"/>
          <path d="M12 5v14"/>
        </svg>
      ),
    };

    const Icon = icons[icon];
    return Icon ? <span className={className}><Icon /></span> : null;
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-favicon-blue">
          {lang === 'en' ? 'Welcome to GM Project Filter System' : '欢迎使用 誉铭集团 项目筛选系统'}
        </h1>
        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
          {lang === 'en' 
            ? 'A comprehensive project evaluation and strategic analysis platform designed to help you make informed decisions about customer projects.'
            : '一个全面的项目评估和战略分析平台，旨在帮助您对客户项目做出明智的决策。'}
        </p>
      </div>

      {/* Architecture Image Section */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            {lang === 'en' ? 'System Architecture' : '系统架构'}
          </h2>
          <p className="text-slate-600">
            {lang === 'en' ? 'Overview of the system components and data flow' : '系统组件和数据流概览'}
          </p>
        </div>
        <div className="relative w-full aspect-video bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
          {/* 图片加载状态指示器 */}
          {imageLoading && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-slate-400 text-sm">
                {lang === 'en' ? 'Loading image...' : '图片加载中...'}
              </div>
            </div>
          )}
          {/* 图片加载错误提示 */}
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-4">
                <div className="text-slate-400 text-sm mb-2">
                  {lang === 'en' ? 'Failed to load image' : '图片加载失败'}
                </div>
                <button
                  onClick={() => {
                    setImageError(false);
                    setImageLoading(true);
                  }}
                  className="text-blue-600 text-sm hover:text-blue-700 underline"
                >
                  {lang === 'en' ? 'Retry' : '重试'}
                </button>
              </div>
            </div>
          )}
          {/* 图片组件 */}
          {!imageError && (
            <Image
              src="/architechture-with-logo.jpg"
              alt={lang === 'en' ? 'System Architecture Diagram' : '系统架构图'}
              fill
              className="object-contain p-4"
              priority
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
              quality={85}
              onLoad={() => setImageLoading(false)}
              onError={(e) => {
                console.error('Image failed to load:', e);
                setImageError(true);
                setImageLoading(false);
              }}
            />
          )}
        </div>
      </div>

      {/* Quick Links Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-slate-800 text-center">
          {lang === 'en' ? 'Quick Access' : '快速入口'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => setActiveTab(link.id)}
              className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 text-left hover:shadow-md hover:border-blue-300 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 group-hover:bg-blue-100 transition-colors">
                  <IconWrapper icon={link.icon} className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 mb-1 group-hover:text-blue-600 transition-colors">
                    {t[link.labelKey as keyof typeof t]}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {link.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* System Information Section */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-sm text-slate-500 mb-1">
              {lang === 'en' ? 'Version' : '版本'}
            </div>
            <div className="text-lg font-semibold text-slate-800">V10.4</div>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">
              {lang === 'en' ? 'Last Updated' : '最后更新'}
            </div>
            <div className="text-lg font-semibold text-slate-800">
              {new Date().toLocaleDateString(lang === 'en' ? 'en-US' : 'zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">
              {lang === 'en' ? 'Status' : '状态'}
            </div>
            <div className="text-lg font-semibold text-green-600">
              {lang === 'en' ? 'Active' : '运行中'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

